// Shared Y-axis auto-scale helper. Backs the `yScale: { mode: "auto" }`
// opt-in on `line`, `bar`, `stacked_bar`, `area`, and `scatter` chart
// specs (see `YScaleAuto` in `sbi.d.ts`). Returning a single shape from
// one helper keeps the five chart components in lockstep — the
// padding-math definition lives here, not duplicated five times.

import type { YScaleAuto } from "../sbi/protocol";

/**
 * Default padding when `yScale: { mode: "auto" }` is set without an
 * explicit `padding`. 10% matches the value documented in `YScaleAuto`
 * — keep the two in sync.
 */
const DEFAULT_PADDING = 0.1;

/**
 * Computes an auto y-axis domain from a flat list of values:
 *
 *   range  = max - min
 *   axis   = [min - range * padding, max + range * padding]
 *
 * Returns `undefined` when the input has no finite values, or when the
 * range is zero (all values equal — a `min === max` axis is degenerate
 * and ECharts' default "nice" domain renders better around a single
 * point). The caller should treat `undefined` as "leave the axis at the
 * library default" rather than "set min/max to 0".
 *
 * `padding` is clamped to `[0, 1]`. The wire-level schema enforces the
 * same range but a runtime clamp keeps the math defensive against any
 * future path that bypasses validation (e.g. a worker shim).
 */
export function computeAutoYDomain(
  values: Iterable<unknown>,
  padding: number = DEFAULT_PADDING,
): { min: number; max: number } | undefined {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (const raw of values) {
    const n = typeof raw === "number" ? raw : Number(raw);
    if (!Number.isFinite(n)) continue;
    if (n < min) min = n;
    if (n > max) max = n;
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) return undefined;
  const range = max - min;
  if (range === 0) return undefined;
  const ratio = Math.max(0, Math.min(1, padding));
  const pad = range * ratio;
  return { min: min - pad, max: max + pad };
}

/**
 * Convenience overload for the common "spec.yScale + flat values"
 * branch. Returns `undefined` when auto-scale isn't engaged so callers
 * can spread the result onto the yAxis object unconditionally.
 */
export function autoYDomainFor(
  yScale: YScaleAuto | undefined,
  values: Iterable<unknown>,
): { min: number; max: number } | undefined {
  if (!yScale || yScale.mode !== "auto") return undefined;
  return computeAutoYDomain(values, yScale.padding ?? DEFAULT_PADDING);
}
